import Add from '@mui/icons-material/Add';
import Delete from '@mui/icons-material/Delete';
import Edit from '@mui/icons-material/Edit';
import MoreHoriz from '@mui/icons-material/MoreHoriz';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import DataGridPager from '~components/DataGrid/DataGridPager';
import actionsColumn from '~components/DataTable/actionsColumn';
import DataTable from '~components/DataTable/DataTable';
import { DataTableColumn } from '~components/DataTable/dataTableColumn';
import ConfirmationDialog from '~components/Dialog/ConfirmationDialog';
import Dialog from '~components/Dialog/Dialog';
import DialogActions from '~components/Dialog/DialogActions';
import DialogContent from '~components/Dialog/DialogContent';
import DialogHeader from '~components/Dialog/DialogHeader';
import { createdBy as principalDisplayName } from '~components/Job/AuditDetails/utils';
import { SimpleMenu } from '~components/Menu/SimpleMenu';
import {
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import { API_BASE_URL, DEFAULT_API_PAGE_SIZE } from '~constants/consts';
import useApiClients from '~hooks/useApiClients';
import { M2MClient } from '~hooks/useApiClients/models';
import { RoleItem } from '~hooks/useUsers/models';
import { useUserRoles } from '~hooks/useUsers/useUserRoles';
import SettingsPageHeader from '~pages/Settings/components/SettingsPageHeader';
import { Pagination, PaginationLink } from '~services/pagination';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import { dateFormat } from '~utils/dateTime';
import { getPageNumber } from '~utils/pagination';
import {
  getEffectiveUserCompanyId,
  getEffectiveUserCompanyName,
} from '~utils/user/user-utils';

import ApiClientForm, { ApiClientFormHandler } from './ApiClientForm';
import { ALLOWED_M2M_ROLE_NAMES } from './apiClientSchema';
import ApiKeyDisplayDialog from './ApiKeyDisplayDialog';
import CopyLlmPromptButton from './CopyLlmPromptButton';

type FormDialogState = {
  client?: M2MClient;
  isOpen: boolean;
  isSubmitting: boolean;
};

type RevokeDialogState = {
  client?: M2MClient;
  isOpen: boolean;
  isSubmitting: boolean;
};

type KeyDisplayState = {
  isOpen: boolean;
  apiKey: string;
  clientName: string;
};

export const ApiClientsManagement = observer(() => {
  const { userStore, toasterStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);
  const companyName = getEffectiveUserCompanyName(userStore);
  const userPermissions = userStore.getPermissions();

  const { fetchApiClients, revokeApiClient, createApiClient, updateApiClient } =
    useApiClients();
  const { getCompanyUserRoles } = useUserRoles();

  const [isLoading, setIsLoading] = useState(false);
  const [apiClients, setApiClients] = useState<M2MClient[]>([]);
  const [pagination, setPagination] = useState<Pagination>({
    limit: DEFAULT_API_PAGE_SIZE,
    page: 1,
  });
  const [availableRoles, setAvailableRoles] = useState<RoleItem[]>([]);
  const [isPreparingForm, setIsPreparingForm] = useState(false);

  const formRef = useRef<ApiClientFormHandler>(null);
  const [formDialogState, setFormDialogState] = useState<FormDialogState>({
    isOpen: false,
    isSubmitting: false,
  });
  const [revokeDialogState, setRevokeDialogState] = useState<RevokeDialogState>({
    isOpen: false,
    isSubmitting: false,
  });
  const [keyDisplay, setKeyDisplay] = useState<KeyDisplayState>({
    isOpen: false,
    apiKey: '',
    clientName: '',
  });

  const fetchData = useCallback(
    async (link?: PaginationLink) => {
      const cursor = link ? pagination[link] : undefined;

      setIsLoading(true);
      try {
        const result = await fetchApiClients({ companyId, link, cursor });

        if (result) {
          setApiClients(result.data);
          setPagination({
            ...result.pagination,
            limit: pagination.limit,
            page: link ? getPageNumber(pagination.page, link) : 1,
          });
        }
      } finally {
        setIsLoading(false);
      }
    },
    [companyId, pagination, fetchApiClients],
  );

  useEffect(() => {
    fetchData();
    setFormDialogState({ isOpen: false, isSubmitting: false });
    setAvailableRoles([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const openFormDialog = useCallback(
    async (client?: M2MClient) => {
      setFormDialogState({ isOpen: true, isSubmitting: false, client });
      setIsPreparingForm(true);
      try {
        const roles = await getCompanyUserRoles(companyId);
        if (roles) {
          setAvailableRoles(
            roles.filter((role) => ALLOWED_M2M_ROLE_NAMES.has(role.name)),
          );
        }
      } catch {
        setFormDialogState((state) => ({ ...state, isOpen: false }));
      } finally {
        setIsPreparingForm(false);
      }
    },
    [companyId, getCompanyUserRoles],
  );

  const handleCreateClick = useCallback(() => {
    void openFormDialog();
  }, [openFormDialog]);

  const handleEditClick = useCallback(
    (client: M2MClient) => {
      void openFormDialog(client);
    },
    [openFormDialog],
  );

  const handleCloseFormDialog = useCallback(() => {
    setFormDialogState((state) => ({ ...state, isOpen: false }));
  }, []);

  const handleSubmitForm = useCallback(async () => {
    const formData = await formRef.current?.onSubmit();
    if (!formData) return;

    setFormDialogState((state) => ({ ...state, isSubmitting: true }));

    const slug = formData.slug || null;

    try {
      if (formDialogState.client) {
        const updated = await updateApiClient({
          companyId,
          apiClientId: formDialogState.client.id,
          name: formData.name,
          slug,
        });
        if (updated) {
          toasterStore.push(
            alert(
              t('administration.api_clients.updated', { name: updated.name }),
              AlertTypes.success,
            ),
          );
          setFormDialogState({ isOpen: false, isSubmitting: false });
          fetchData();
        } else {
          setFormDialogState((state) => ({ ...state, isSubmitting: false }));
        }
      } else {
        const result = await createApiClient({
          companyId,
          name: formData.name,
          iamRoleIds: formData.roles.map((role) => role.id),
          slug,
        });
        if (result) {
          toasterStore.push(
            alert(
              t('administration.api_clients.created', { name: result.apiClient.name }),
              AlertTypes.success,
            ),
          );
          setFormDialogState({ isOpen: false, isSubmitting: false });
          setKeyDisplay({
            isOpen: true,
            apiKey: result.plaintextApiKey,
            clientName: result.apiClient.name,
          });
          fetchData();
        } else {
          setFormDialogState((state) => ({ ...state, isSubmitting: false }));
        }
      }
    } catch {
      setFormDialogState((state) => ({ ...state, isSubmitting: false }));
    }
  }, [
    formDialogState.client,
    companyId,
    createApiClient,
    updateApiClient,
    toasterStore,
    fetchData,
  ]);

  const handleCloseKeyDisplay = useCallback(() => {
    setKeyDisplay({ isOpen: false, apiKey: '', clientName: '' });
  }, []);

  const handleRevokeClick = useCallback((client: M2MClient) => {
    setRevokeDialogState({ isOpen: true, isSubmitting: false, client });
  }, []);

  const handleCloseRevokeDialog = useCallback(() => {
    setRevokeDialogState((state) => ({ ...state, isOpen: false }));
  }, []);

  const handleConfirmRevoke = useCallback(async () => {
    const client = revokeDialogState.client;
    if (!client) return;

    setRevokeDialogState((state) => ({ ...state, isSubmitting: true }));

    try {
      await revokeApiClient({ companyId, apiClientId: client.id });
      toasterStore.push(
        alert(
          t('administration.api_clients.revoked', { name: client.name }),
          AlertTypes.success,
        ),
      );
      setRevokeDialogState({ isOpen: false, isSubmitting: false });
      fetchData();
    } catch {
      setRevokeDialogState((state) => ({ ...state, isSubmitting: false }));
    }
  }, [revokeDialogState.client, companyId, revokeApiClient, toasterStore, fetchData]);

  const columns = useMemo<Array<DataTableColumn<M2MClient>>>(
    () => [
      {
        field: 'name',
        headerName: t('form_fields.name'),
        valueGetter: (client) => client.name,
      },
      {
        field: 'slug',
        headerName: t('administration.api_clients.column_slug'),
        valueGetter: (client) => client.slug ?? '',
      },
      {
        field: 'apiKey',
        headerName: t('administration.api_clients.column_api_key'),
        valueGetter: (client) =>
          client.apiKeyLastFour ? `••••${client.apiKeyLastFour}` : '—',
      },
      {
        field: 'roles',
        headerName: t('administration.api_clients.column_roles'),
        valueGetter: (client) => client.iamRoles.map((role) => role.name).join(', '),
      },
      {
        field: 'createdBy',
        headerName: t('administration.api_clients.column_created_by'),
        valueGetter: (client) => principalDisplayName(client.createdBy),
      },
      {
        field: 'createdAt',
        headerName: t('common.created_on'),
        valueGetter: (client) => dateFormat(client.createdAt),
      },
      actionsColumn((client) => {
        const revokeOptions = userPermissions.canRevokeM2MClient
          ? [
              {
                title: t('administration.api_clients.revoke'),
                icon: <Delete />,
                callBack: () => handleRevokeClick(client),
              },
            ]
          : [];
        return (
          <>
            {userPermissions.canEditM2MClient && (
              <IconButton
                size="small"
                data-ui={`page-settings-api-clients__management__${client.id}__edit-button`}
                disabled={isPreparingForm || formDialogState.isOpen}
                onClick={() => handleEditClick(client)}
              >
                <Edit />
              </IconButton>
            )}
            {revokeOptions.length > 0 && (
              <SimpleMenu options={revokeOptions}>
                <MoreHoriz />
              </SimpleMenu>
            )}
          </>
        );
      }),
    ],
    [
      formDialogState.isOpen,
      isPreparingForm,
      userPermissions.canRevokeM2MClient,
      userPermissions.canEditM2MClient,
      handleRevokeClick,
      handleEditClick,
    ],
  );

  return (
    <>
      <SettingsPageHeader
        title={t('settings.nav_items.api_creds')}
        description={t('settings.page_descriptions.api_creds')}
      />
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 2 }}>
        <CopyLlmPromptButton
          apiBaseUrl={API_BASE_URL}
          companyId={companyId}
          companyName={companyName}
        />
        <SmallButton
          color="brandV2Yellow"
          data-ui="page-settings-api-clients__management__create-button"
          disabled={
            !userPermissions.canCreateM2MClient ||
            isPreparingForm ||
            formDialogState.isOpen
          }
          onClick={handleCreateClick}
          startIcon={<Add />}
        >
          {t('administration.api_clients.create_button')}
        </SmallButton>
      </Box>
      <Box data-test-id={'api-clients-data-grid'}>
        <DataTable
          id="api-clients-data-grid"
          columns={columns}
          rows={apiClients}
          loading={isLoading}
          headerTrailingComponent={
            <DataGridPager
              isFetching={isLoading}
              onNavigate={fetchData}
              pagination={pagination}
            />
          }
        />
      </Box>
      <Dialog
        isOpen={formDialogState.isOpen}
        preventCloseOnBackdropClick
        preventCloseOnEscapeKeyDown
      >
        <DialogHeader onClose={handleCloseFormDialog}>
          {formDialogState.client
            ? t('administration.api_clients.edit_dialog_title')
            : t('administration.api_clients.create_dialog_title')}
        </DialogHeader>

        <DialogContent>
          {isPreparingForm ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <>
              <ApiClientForm
                ref={formRef}
                apiClient={formDialogState.client}
                availableRoles={availableRoles}
              />
              {formDialogState.client && (
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                    mt: 1,
                    display: 'block',
                  }}
                >
                  {t('administration.api_clients.roles_locked_helper')}
                </Typography>
              )}
            </>
          )}
        </DialogContent>

        <DialogActions>
          <SmallButton
            color="secondary"
            data-ui="page-settings-api-clients__form__cancel-button"
            disabled={formDialogState.isSubmitting}
            onClick={handleCloseFormDialog}
            variant="outlined"
          >
            {t('actions.cancel')}
          </SmallButton>

          <SmallLoadingButton
            data-ui="page-settings-api-clients__form__submit-button"
            disabled={isPreparingForm}
            loading={formDialogState.isSubmitting}
            onClick={handleSubmitForm}
          >
            {formDialogState.client ? t('actions.update') : t('actions.create')}
          </SmallLoadingButton>
        </DialogActions>
      </Dialog>
      <ApiKeyDisplayDialog
        isOpen={keyDisplay.isOpen}
        apiKey={keyDisplay.apiKey}
        clientName={keyDisplay.clientName}
        onDone={handleCloseKeyDisplay}
      />
      <ConfirmationDialog
        isBusy={revokeDialogState.isSubmitting}
        isOpen={revokeDialogState.isOpen}
        onClose={handleCloseRevokeDialog}
        onConfirm={handleConfirmRevoke}
        title={t('administration.api_clients.revoke_confirm_title')}
      >
        <Typography>
          {t('administration.api_clients.revoke_confirm_message', {
            name: revokeDialogState.client?.name ?? '',
          })}
        </Typography>
      </ConfirmationDialog>
    </>
  );
});
